import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface ProductRow {
  sku: string;
  name_en: string;
  step_label: string;
  msrp_bdt: string;
  status: string;
  hasContent: boolean;
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [coreRes, contentRes] = await Promise.all([
      supabase.from('rxk_public_products_v').select('sku, name_en, step_label, msrp_bdt, status'),
      supabase.from('rxk_product_content').select('sku'),
    ]);
    const contentSkus = new Set((contentRes.data as { sku: string }[] ?? []).map((r) => r.sku));
    const merged = ((coreRes.data as Omit<ProductRow, 'hasContent'>[]) ?? []).map((r) => ({
      ...r,
      hasContent: contentSkus.has(r.sku),
    }));
    setRows(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Products — Content</h1>
        </div>

        <Card>
          <CardHeader
            title="RxK SKUs"
            subtitle="Pricing, name, and status come from Core ERP and aren't editable here — only the storefront content below is."
            icon={<Package className="w-4 h-4" />}
          />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : rows.length === 0 ? (
              <EmptyState title="No SKUs found" />
            ) : (
              rows.map((r) => (
                <button
                  key={r.sku}
                  onClick={() => navigate(`/admin/products/${r.sku}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">{r.name_en}</p>
                    <p className="text-[11px] text-ink-faint truncate">{r.sku} · {r.step_label} · ৳{Number(r.msrp_bdt).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!r.hasContent && <Badge tone="warning">No content yet</Badge>}
                    <Badge tone={r.status === 'active' ? 'success' : 'neutral'}>{r.status}</Badge>
                  </div>
                </button>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
